import { Button, Label } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { tv } from 'tailwind-variants'
import { MembershipIconCircled } from 'src/components/Membership/MembershipIcon'
import { openMembershipMoreCreditsModal } from 'src/components/Membership/MembershipMoreCreditsModal'
import { UITooltipTriggerWrapper } from 'src/components/ui/Tooltip'
import { GenerationModelType } from 'src/services/apollo/generated/graphql'
import { useMe } from 'src/services/helpers/user'
import type { SizeRange } from 'src/services/orpc/sizeConfig'
import { cx } from 'src/utils/cx'
import {
  ASPECT_RATIOS,
  type AspectRatio,
  calculateSizeFromRatioAndTier,
  getOrientedAspectRatio,
  type Orientation,
  SIZE_TIER_CONFIG,
  type SizeTier,
} from '../../../constants'
import {
  calculateSizeFromRatioAndRange,
  type AspectRatioWithPresetInfo,
} from './apiAdapter'
import { selectableButton } from './styles'

const segmentButton = tv({
  base: 'flex items-center justify-center py-2 px-3 transition-all cursor-pointer flex-1',
  variants: {
    selected: {
      true: 'bg-neutral-700 dark:bg-neutral-300 text-white dark:text-neutral-900',
      false:
        'bg-neutral-100 dark:bg-neutral-700 hover:bg-neutral-200 dark:hover:bg-neutral-600',
    },
  },
})

/** Renders a small rectangle icon representing the aspect ratio */
export function AspectRatioIcon({ ratio }: { ratio: AspectRatio }) {
  const { width, height } = ratio
  return (
    <div className="w-5 h-5 flex items-center justify-center">
      <div
        className="border-2 dark:border-zinc-300 border-zinc-500 rounded-sm"
        style={{
          width: width >= height ? '100%' : `${(width / height) * 100}%`,
          height: height >= width ? '100%' : `${(height / width) * 100}%`,
        }}
      />
    </div>
  )
}

export function AspectRatioSelector(props: {
  selectedRatio: AspectRatio
  orientation: Orientation
  onRatioChange: (ratio: AspectRatio) => void
  onOrientationChange: (orientation: Orientation) => void
  /** V2 API-driven ratios for the current range */
  apiRatios?: AspectRatioWithPresetInfo[]
  /** V2 only — parent range's requiredMembershipTier, suppresses redundant per-preset icons */
  rangeRequiredMembershipTier?: number
  /** @deprecated V1 only — use apiRatios for V2 */
  extraRatios?: AspectRatio[]
  /** @deprecated V1 only — use apiRatios for V2 */
  tier?: SizeTier
}) {
  const {
    selectedRatio,
    orientation,
    onRatioChange,
    onOrientationChange,
    apiRatios,
    rangeRequiredMembershipTier,
    extraRatios,
    tier,
  } = props
  const { t } = useTranslation(['generation'])
  const { beingMember } = useMe()

  const isRatioSelected = (baseRatio: AspectRatio) => {
    const orientedRatio = getOrientedAspectRatio(baseRatio, orientation)
    return (
      orientedRatio.width === selectedRatio.width &&
      orientedRatio.height === selectedRatio.height
    )
  }

  // V1 fallback
  const v1Ratios = apiRatios ? undefined : ASPECT_RATIOS
  const v1ExtraRatios = apiRatios ? undefined : extraRatios

  return (
    <section>
      <Label className="block mb-2 text-sm font-medium">
        {t('generation:size.aspect-ratio.label', 'Aspect Ratio')}
      </Label>
      <div className="flex flex-col gap-2">
        {/* Orientation toggle */}
        <div className="flex rounded-lg overflow-hidden ring-1 ring-neutral-50 dark:ring-neutral-900">
          <Button
            className={segmentButton({ selected: orientation === 'portrait' })}
            onPress={() => onOrientationChange('portrait')}
          >
            <span className="text-sm font-medium">
              {t('generation:size.orientation.portrait', 'Portrait')}
            </span>
          </Button>
          <Button
            className={segmentButton({ selected: orientation === 'landscape' })}
            onPress={() => onOrientationChange('landscape')}
          >
            <span className="text-sm font-medium">
              {t('generation:size.orientation.landscape', 'Landscape')}
            </span>
          </Button>
        </div>

        {/* V2 API-driven ratios — all non-hidden presets shown flat in modal */}
        {apiRatios && (
          <div className="grid grid-cols-4 gap-2">
            {apiRatios.map(ratio => (
              <ApiRatioButton
                key={ratio.label}
                ratio={ratio}
                orientation={orientation}
                selected={isRatioSelected(ratio)}
                isMember={beingMember}
                rangeRequiredMembershipTier={rangeRequiredMembershipTier ?? 0}
                onPress={onRatioChange}
              />
            ))}
          </div>
        )}

        {/* V1 fallback ratios */}
        {v1Ratios && (
          <div className="grid grid-cols-4 gap-2">
            {v1Ratios.map(baseRatio => {
              const displayRatio = getOrientedAspectRatio(
                baseRatio,
                orientation,
              )
              const isSquare = baseRatio.width === baseRatio.height
              const selected = isRatioSelected(baseRatio)

              return (
                <Button
                  key={baseRatio.label}
                  className={selectableButton({
                    selected,
                    className:
                      'flex items-center justify-center gap-1.5 py-2 px-3',
                  })}
                  onPress={() => onRatioChange(displayRatio)}
                >
                  <AspectRatioIcon ratio={displayRatio} />
                  <span className="text-sm font-medium">
                    {isSquare ? baseRatio.label : displayRatio.label}
                  </span>
                </Button>
              )
            })}
            {v1ExtraRatios?.map(baseRatio => {
              const displayRatio = getOrientedAspectRatio(
                baseRatio,
                orientation,
              )
              const selected = isRatioSelected(baseRatio)
              const disabled =
                baseRatio.availableAt &&
                tier &&
                !baseRatio.availableAt.includes(tier)

              const button = (
                <Button
                  key={baseRatio.label}
                  className={selectableButton({
                    selected: !disabled && selected,
                    className: cx(
                      'flex items-center justify-center gap-1.5 py-2 px-3',
                      disabled && 'opacity-50 cursor-not-allowed',
                    ),
                  })}
                  onPress={() => {
                    if (!disabled) onRatioChange(displayRatio)
                  }}
                >
                  <AspectRatioIcon ratio={displayRatio} />
                  <span className="text-sm font-medium">
                    {displayRatio.label}
                  </span>
                </Button>
              )

              if (disabled) {
                return (
                  <UITooltipTriggerWrapper
                    key={baseRatio.label}
                    placement="bottom"
                    delay={200}
                    trigger={button}
                  >
                    <div className="px-3 py-2">
                      {t(
                        'generation:size.aspect-ratio.large-only',
                        'Only available in Large size',
                      )}
                    </div>
                  </UITooltipTriggerWrapper>
                )
              }

              return button
            })}
          </div>
        )}
      </div>
    </section>
  )
}

/** Ratio button for V2 API-driven mode with membership support */
function ApiRatioButton(props: {
  ratio: AspectRatioWithPresetInfo
  orientation: Orientation
  selected: boolean
  isMember: boolean
  rangeRequiredMembershipTier: number
  onPress: (ratio: AspectRatio) => void
}) {
  const {
    ratio,
    orientation,
    selected,
    isMember,
    rangeRequiredMembershipTier,
    onPress,
  } = props
  const displayRatio = getOrientedAspectRatio(ratio, orientation)
  const isSquare = ratio.width === ratio.height
  const locked = ratio.accessStatus === 'membership_required' && !isMember
  // Only show icon when preset requires higher tier than the range itself
  const isPremium = ratio.requiredMembershipTier > rangeRequiredMembershipTier
  const displayLabel = isSquare ? ratio.label : displayRatio.label

  return (
    <Button
      className={selectableButton({
        selected: !locked && selected,
        className: cx(
          'relative flex items-center justify-center gap-1.5 py-2 px-3',
          locked && 'opacity-70',
        ),
      })}
      onPress={() => {
        if (locked) {
          openMembershipMoreCreditsModal({
            defaultMode: 'plans',
            trackingLocation: 'dimension-size',
          })
        } else {
          onPress(displayRatio)
        }
      }}
    >
      <AspectRatioIcon ratio={displayRatio} />
      <span className="text-sm font-medium">{displayLabel}</span>
      {isPremium && (
        <MembershipIconCircled className="absolute top-0.5 right-0.5 size-3.5" />
      )}
    </Button>
  )
}

const SIZE_TIERS: SizeTier[] = ['medium', 'large']

export function SizeTierSelector(props: {
  selectedTier: SizeTier | string
  selectedRatio: AspectRatio
  onChange: (tier: SizeTier | string) => void
  modelType: GenerationModelType
  isMember: boolean
  divisor: number
  label?: string
  currentSize?: { width: number; height: number }
  /** V2 API-driven ranges (when provided, replaces SIZE_TIER_CONFIG) */
  apiRanges?: SizeRange[]
}) {
  const {
    selectedTier,
    selectedRatio,
    onChange,
    modelType,
    isMember,
    divisor,
    label,
    currentSize,
    apiRanges,
  } = props
  const { t } = useTranslation(['generation'])

  if (apiRanges) {
    const visibleRanges = apiRanges.filter(r => r.displayMode !== 'hidden')
    return (
      <section>
        <Label className="block mb-2 text-sm font-medium">
          {label ?? t('generation:size.tier.label', 'Size')}
        </Label>
        <div className="flex gap-2">
          {visibleRanges.map(range => {
            const locked =
              range.accessStatus === 'membership_required' && !isMember
            const isPremium = range.requiredMembershipTier > 0
            const size = calculateSizeFromRatioAndRange(selectedRatio, range)

            return (
              <Button
                key={range.name}
                className={selectableButton({
                  selected: selectedTier === range.name,
                  className: cx(
                    'relative flex-1 py-3 px-4',
                    locked && 'opacity-70',
                  ),
                })}
                onPress={() => {
                  if (locked) {
                    openMembershipMoreCreditsModal({
                      defaultMode: 'plans',
                      trackingLocation: 'dimension-size',
                    })
                  } else {
                    onChange(range.name)
                  }
                }}
              >
                <div className="text-center">
                  <div className="text-base font-semibold">{range.name}</div>
                  <div className="text-xs text-neutral-500 dark:text-neutral-400">
                    {selectedTier === range.name && currentSize
                      ? `${currentSize.width}×${currentSize.height}`
                      : `${size.width}×${size.height}`}
                  </div>
                </div>
                {isPremium && (
                  <MembershipIconCircled className="absolute top-1 right-1 size-4" />
                )}
              </Button>
            )
          })}
        </div>
      </section>
    )
  }

  const config =
    SIZE_TIER_CONFIG[modelType as keyof typeof SIZE_TIER_CONFIG] ??
    SIZE_TIER_CONFIG[GenerationModelType.SdxlModel]

  const tierLabels: Record<SizeTier, string> = {
    medium: t('generation:size.tier.medium', 'M'),
    large: t('generation:size.tier.large', 'L'),
  }

  return (
    <section>
      <Label className="block mb-2 text-sm font-medium">
        {label ?? t('generation:size.tier.label', 'Size')}
      </Label>
      <div className="flex gap-2">
        {SIZE_TIERS.map(tier => {
          const { memberOnly } = config[tier]
          const disabled = memberOnly && !isMember
          const size = calculateSizeFromRatioAndTier(
            selectedRatio,
            tier,
            modelType,
            divisor,
          )

          return (
            <Button
              key={tier}
              className={selectableButton({
                selected: selectedTier === tier,
                className: cx(
                  'relative flex-1 py-3 px-4',
                  disabled && 'opacity-70',
                ),
              })}
              onPress={() => {
                if (disabled) {
                  openMembershipMoreCreditsModal({
                    defaultMode: 'plans',
                    trackingLocation: 'dimension-size',
                  })
                } else {
                  onChange(tier)
                }
              }}
            >
              <div className="text-center">
                <div className="text-base font-semibold">
                  {tierLabels[tier]}
                </div>
                <div className="text-xs text-neutral-500 dark:text-neutral-400">
                  {selectedTier === tier && currentSize
                    ? `${currentSize.width}×${currentSize.height}`
                    : `${size.width}×${size.height}`}
                </div>
              </div>
              {memberOnly && (
                <MembershipIconCircled className="absolute top-1 right-1 size-4" />
              )}
            </Button>
          )
        })}
      </div>
    </section>
  )
}
